/**
 * StatisticsModal - Display quiz statistics in a modal
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';

function StatisticsModal({ 
  isOpen, 
  onClose, 
  participants, 
  answers, 
  questions,
  currentQuestion = 0
}) {
  const { isDark } = useTheme();

  // Handle both old and new prop formats
  const actualParticipants = participants || {};
  const actualAnswers = answers || {};
  const actualQuestions = questions || [];
  
  if (!isOpen) return null;

  if (!actualQuestions || actualQuestions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
        <div 
          className={`rounded-xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex justify-between items-center p-4 ${
            isDark ? 'border-b border-gray-700' : 'border-b border-gray-300'
          }`}>
            <h3 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Statistiken
            </h3>
            <button 
              onClick={onClose} 
              className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 theme-transition ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-8 text-center">
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Keine Daten verfügbar</p>
          </div>
        </div>
      </div>
    );
  }

  const participantCount = Object.keys(actualParticipants).length;
  
  // Only count questions that have been played (up to and including currentQuestion)
  const playedQuestions = actualQuestions.slice(0, currentQuestion + 1);
  const playedCount = playedQuestions.length;

  // Calculate overall statistics for played questions only
  let totalAnswers = 0;
  let totalCorrect = 0;
  let totalPoints = 0;
  let totalResponseTime = 0;
  let responseTimeCount = 0;
  let participationSum = 0;
  
  // Time tracking
  let fastestTime = Infinity;
  let slowestTime = 0;
  let fastestAnswer = null;
  let slowestAnswer = null;

  playedQuestions.forEach((question, index) => {
    const questionAnswers = actualAnswers[index] || {};
    const answerCount = Object.keys(questionAnswers).length;
    totalAnswers += answerCount;
    
    if (participantCount > 0) {
      participationSum += (answerCount / participantCount);
    }

    Object.entries(questionAnswers).forEach(([odId, answer]) => {
      if (answer.isCorrect) {
        totalCorrect++;
      }
      
      if (answer.pointsEarned) {
        totalPoints += answer.pointsEarned;
      }
      
      if (answer.responseTimeMs && answer.responseTimeMs > 0 && answer.responseTimeMs < 300000) {
        const timeInSec = answer.responseTimeMs / 1000;
        totalResponseTime += timeInSec;
        responseTimeCount++;
        
        if (timeInSec < fastestTime) {
          fastestTime = timeInSec;
          fastestAnswer = {
            odId,
            questionIndex: index,
            time: timeInSec,
            isCorrect: answer.isCorrect,
            participantName: actualParticipants[odId]?.name || 'Unbekannt'
          };
        }
        
        if (timeInSec > slowestTime) {
          slowestTime = timeInSec;
          slowestAnswer = {
            odId,
            questionIndex: index,
            time: timeInSec,
            isCorrect: answer.isCorrect,
            participantName: actualParticipants[odId]?.name || 'Unbekannt'
          };
        }
      }
    });
  });

  const overallCorrectRate = totalAnswers > 0 
    ? Math.round((totalCorrect / totalAnswers) * 100) 
    : 0;
  
  const avgResponseTime = responseTimeCount > 0 
    ? (totalResponseTime / responseTimeCount).toFixed(1) 
    : null;

  const avgPointsPerAnswer = totalAnswers > 0
    ? Math.round(totalPoints / totalAnswers)
    : 0;

  // Find hardest and easiest questions
  const questionStats = playedQuestions.map((question, index) => {
    const questionAnswers = actualAnswers[index] || {};
    const answerCount = Object.keys(questionAnswers).length;
    let correctCount = 0;
    let questionTotalTime = 0;
    let questionTimeCount = 0;

    Object.values(questionAnswers).forEach(answer => {
      if (answer.isCorrect) {
        correctCount++;
      }
      if (answer.responseTimeMs && answer.responseTimeMs > 0) {
        questionTotalTime += answer.responseTimeMs / 1000;
        questionTimeCount++;
      }
    });

    return {
      questionIndex: index,
      answerCount,
      correctCount,
      correctPercentage: answerCount > 0 ? Math.round((correctCount / answerCount) * 100) : 0,
      avgTime: questionTimeCount > 0 ? (questionTotalTime / questionTimeCount).toFixed(1) : null
    };
  });

  const answeredQuestions = questionStats.filter(q => q.answerCount > 0);
  const hardestQuestion = answeredQuestions.length > 0
    ? answeredQuestions.reduce((min, q) => q.correctPercentage < min.correctPercentage ? q : min)
    : null;
  const easiestQuestion = answeredQuestions.length > 0
    ? answeredQuestions.reduce((max, q) => q.correctPercentage > max.correctPercentage ? q : max)
    : null;
    
  const questionsWithTime = questionStats.filter(q => q.avgTime !== null);
  const fastestQuestion = questionsWithTime.length > 0
    ? questionsWithTime.reduce((min, q) => parseFloat(q.avgTime) < parseFloat(min.avgTime) ? q : min)
    : null;
  const slowestQuestion = questionsWithTime.length > 0
    ? questionsWithTime.reduce((max, q) => parseFloat(q.avgTime) > parseFloat(max.avgTime) ? q : max)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className={`rounded-xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex justify-between items-center p-4 ${
          isDark ? 'border-b border-gray-700' : 'border-b border-gray-300'
        }`}>
          <h3 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Statistiken
          </h3>
          <button 
            onClick={onClose} 
            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 theme-transition ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {/* Progress */}
            <div className={`text-center text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Quiz-Fortschritt: {playedCount} von {actualQuestions.length} Fragen gespielt
            </div>
            
            {/* Main Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 text-center ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-50'
              }`}>
                <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                  {participantCount}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Teilnehmer
                </div>
              </div>
              
              <div className={`rounded-lg p-3 text-center ${
                isDark ? 'bg-indigo-900/30' : 'bg-indigo-50'
              }`}>
                <div className={`text-2xl font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                  {totalAnswers}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Antworten gesamt
                </div>
              </div>
              
              <div className={`rounded-lg p-3 text-center ${
                isDark ? 'bg-green-900/30' : 'bg-green-50'
              }`}>
                <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  {overallCorrectRate}%
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Ø Richtig
                </div>
              </div>
              
              <div className={`rounded-lg p-3 text-center ${
                isDark ? 'bg-yellow-900/30' : 'bg-yellow-50'
              }`}>
                <div className={`text-2xl font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {avgPointsPerAnswer}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Ø Punkte/Antwort
                </div>
              </div>
            </div>

            {/* Average Response Time */}
            {avgResponseTime && (
              <div className="grid grid-cols-3 gap-2">
                <div className={`rounded-lg p-2 text-center ${
                  isDark ? 'bg-green-900/30' : 'bg-green-50'
                }`}>
                  <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    {fastestTime !== Infinity ? fastestTime.toFixed(1) : '-'}s
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Schnellste
                  </div>
                </div>
                <div className={`rounded-lg p-2 text-center ${
                  isDark ? 'bg-orange-900/30' : 'bg-orange-50'
                }`}>
                  <div className={`text-lg font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                    {avgResponseTime}s
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Ø Zeit
                  </div>
                </div>
                <div className={`rounded-lg p-2 text-center ${
                  isDark ? 'bg-red-900/30' : 'bg-red-50'
                }`}>
                  <div className={`text-lg font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                    {slowestTime > 0 ? slowestTime.toFixed(1) : '-'}s
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Langsamste
                  </div>
                </div>
              </div>
            )}
            
            {/* Total Points */}
            {totalPoints > 0 && (
              <div className={`rounded-lg p-3 text-center ${
                isDark ? 'bg-purple-900/30' : 'bg-purple-50'
              }`}>
                <div className={`text-xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                  {totalPoints} Punkte
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Insgesamt vergeben
                </div>
              </div>
            )}

            {/* Insights */}
            {(answeredQuestions.length > 1 || fastestAnswer || slowestAnswer) && (
              <div className={`pt-3 space-y-2 ${isDark ? 'border-t border-gray-700' : 'border-t border-gray-300'}`}>
                <div className={`text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Erkenntnisse
                </div>
                
                {hardestQuestion && (
                  <div className={`flex items-center text-sm rounded-lg p-2 ${
                    isDark ? 'bg-red-900/30' : 'bg-red-50'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-red-500 mr-3 flex-shrink-0"></div>
                    <div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Schwierigste:</span>
                      <span className={`ml-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Frage {hardestQuestion.questionIndex + 1}
                      </span>
                      <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({hardestQuestion.correctPercentage}% richtig)
                      </span>
                    </div>
                  </div>
                )}

                {easiestQuestion && hardestQuestion !== easiestQuestion && (
                  <div className={`flex items-center text-sm rounded-lg p-2 ${
                    isDark ? 'bg-green-900/30' : 'bg-green-50'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-3 flex-shrink-0"></div>
                    <div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Einfachste:</span>
                      <span className={`ml-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Frage {easiestQuestion.questionIndex + 1}
                      </span>
                      <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({easiestQuestion.correctPercentage}% richtig)
                      </span>
                    </div>
                  </div>
                )}
                
                {fastestAnswer && (
                  <div className={`flex items-center text-sm rounded-lg p-2 ${
                    isDark ? 'bg-cyan-900/30' : 'bg-cyan-50'
                  }`}>
                    <svg className="w-4 h-4 mr-2 flex-shrink-0 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div className="flex-1">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Schnellste Antwort:</span>
                      <span className={`ml-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {fastestAnswer.participantName}
                      </span>
                      <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({fastestAnswer.time.toFixed(1)}s, Frage {fastestAnswer.questionIndex + 1})
                      </span>
                      {fastestAnswer.isCorrect && (
                        <svg className="inline-block w-4 h-4 ml-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                )}

                {slowestAnswer && fastestAnswer !== slowestAnswer && (
                  <div className={`flex items-center text-sm rounded-lg p-2 ${
                    isDark ? 'bg-amber-900/30' : 'bg-amber-50'
                  }`}>
                    <svg className="w-4 h-4 mr-2 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Langsamste Antwort:</span>
                      <span className={`ml-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {slowestAnswer.participantName}
                      </span>
                      <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({slowestAnswer.time.toFixed(1)}s, Frage {slowestAnswer.questionIndex + 1})
                      </span>
                      {slowestAnswer.isCorrect && (
                        <svg className="inline-block w-4 h-4 ml-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                )}
                
                {fastestQuestion && slowestQuestion && fastestQuestion !== slowestQuestion && (
                  <>
                    <div className={`flex items-center text-sm rounded-lg p-2 ${
                      isDark ? 'bg-teal-900/30' : 'bg-teal-50'
                    }`}>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <div>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Schnellste Frage:</span>
                        <span className={`ml-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Frage {fastestQuestion.questionIndex + 1}
                        </span>
                        <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          (Ø {fastestQuestion.avgTime}s)
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center text-sm rounded-lg p-2 ${
                      isDark ? 'bg-orange-900/30' : 'bg-orange-50'
                    }`}>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Längste Überlegung:</span>
                        <span className={`ml-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Frage {slowestQuestion.questionIndex + 1}
                        </span>
                        <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          (Ø {slowestQuestion.avgTime}s)
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Overall Progress Bar */}
            <div className={`pt-3 ${isDark ? 'border-t border-gray-700' : 'border-t border-gray-300'}`}>
              <div className={`flex justify-between text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <span>Gesamterfolgsrate</span>
                <span>{totalCorrect} / {totalAnswers} richtig</span>
              </div>
              <div className={`rounded-full h-3 overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div 
                  className={`h-full transition-all duration-500 ${
                    overallCorrectRate >= 70 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                    overallCorrectRate >= 40 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                    'bg-gradient-to-r from-red-400 to-red-600'
                  }`}
                  style={{ width: `${overallCorrectRate}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`p-4 flex justify-end ${
          isDark ? 'border-t border-gray-700 bg-gray-800/50' : 'border-t border-gray-300 bg-gray-50'
        }`}>
          <button
            onClick={onClose}
            className={`px-5 py-2 rounded-lg theme-transition ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

StatisticsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  participants: PropTypes.object.isRequired,
  answers: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  currentQuestion: PropTypes.number
};

export default StatisticsModal;
